import { darken, lighten } from 'polished';

interface ValidationStyles {
  fill: string;
  fillLight: string;
  text: string;
  border: string;
}

interface BrandStyles {
  primary: string;
  secondary: string;
  tertiary: string;
  quaternary: string;
  quinary: string;
  senary: string;
  septenery: string;
}

interface ButtonStyleDef {
  bg: string;
  fg: string;
  border: string;
  bgHover: string;
  borderHover: string;
}

interface ColourTokens {
  button: {
    primary: ButtonStyleDef;
    secondary: ButtonStyleDef;
    footer: ButtonStyleDef;
    disabled: ButtonStyleDef;
  };
  neutrals: {
    white: string;
    grey1: string;
    grey2: string;
    grey3: string;
    grey4: string;
    grey5: string;
    grey6: string;
    black: string;
  };
  table: {
    background: string;
    hover: string;
    border: string;
  };
  validation: {
    error: ValidationStyles;
    info: ValidationStyles;
    warning: ValidationStyles;
    success: ValidationStyles;
  };
  brand: BrandStyles;
  fill: {
    primary: string;
    secondary: string;
    tertiary: string;
    inverted: string;
  };
  typography: {
    default: string;
    inverted: string;
    subtle: string;
    emphasis: string;
    placeholder: string;
    disabled: string;
    link: string;
    linkVisited: string;
  };
  accessibility: {
    blue: string;
  };
}

const brand: BrandStyles = {
  primary: '#d20a11',
  secondary: '#00313d',
  tertiary: '#d8d2c4',
  quaternary: '#6fb6ba',
  quinary: '#b1d9c3',
  senary: '#eb5e54',
  septenery: '#f6c900',
};

const LightMode: ColourTokens = {
  button: {
    primary: {
      bg: brand.primary,
      fg: '#ffffff',
      bgHover: darken(0.1, brand.primary),
      border: brand.primary,
      borderHover: darken(0.1, brand.primary),
    },
    secondary: {
      bg: 'transparent',
      fg: brand.primary,
      bgHover: 'transparent',
      border: brand.primary,
      borderHover: darken(0.1, brand.primary),
    },
    footer: {
      bg: 'transparent',
      fg: '#333333',
      bgHover: 'transparent',
      border: '#eaeff5',
      borderHover: 'none',
    },
    disabled: {
      bg: '#E0E0E0',
      fg: '#595959',
      bgHover: '#E0E0E0',
      border: '#E0E0E0',
      borderHover: '#E0E0E0',
    },
  },
  neutrals: {
    white: '#ffffff',
    grey1: '#f7f9fd',
    grey2: '#eaeff5',
    grey3: '#d6dce3',
    grey4: '#828282',
    grey5: '#4f4f4f',
    grey6: '#333333',
    black: '#1d1d1b',
  },
  table: {
    background: 'transparent',
    hover: '#f7f9fd',
    border: 'transparent',
  },
  brand,
  fill: {
    primary: '#ffffff',
    secondary: '#eaeff5',
    tertiary: '#ecf5ff',
    inverted: '#1d1d1b',
  },
  typography: {
    default: '#1d1d1b',
    inverted: '#ffffff',
    subtle: '#747373',
    emphasis: brand.primary,
    placeholder: '#4f4f4f',
    disabled: '#4f4f4f',
    link: brand.primary,
    linkVisited: brand.senary,
  },
  validation: {
    error: {
      fill: '#e42929',
      fillLight: '#ffedf0',
      text: '#ffedf0',
      border: '#ff7591',
    },
    info: {
      fill: '#218dcc',
      fillLight: '#ecfafe',
      text: '#ecfafe',
      border: '#4fc3f7',
    },
    success: {
      fill: '#15ad5c',
      fillLight: '#e6fff2',
      text: '#e6fff2',
      border: '#2ee68c',
    },
    warning: {
      fill: '#ffc000',
      fillLight: '#fffff0',
      text: '#1e1e1e',
      border: '#ffee5c',
    },
  },
  accessibility: { blue: '#304ffe' },
};

const DarkMode: ColourTokens = {
  button: {
    primary: {
      bg: darken(0.1, brand.primary),
      fg: '#ffffff',
      bgHover: lighten(0.2, brand.primary),
      border: brand.primary,
      borderHover: lighten(0.2, brand.primary),
    },
    secondary: {
      bg: 'transparent',
      fg: '#ffffff',
      bgHover: 'transparent',
      border: brand.primary,
      borderHover: lighten(0.2, brand.primary),
    },
    footer: {
      bg: 'transparent',
      fg: '#ffffff',
      bgHover: 'transparent',
      border: '#262635',
      borderHover: 'none',
    },
    disabled: {
      bg: '#595959',
      fg: '#E0E0E0',
      bgHover: '#595959',
      border: '#595959',
      borderHover: '#595959',
    },
  },
  neutrals: {
    white: '#1d1d1b',
    grey1: '#222233',
    grey2: '#262635',
    grey3: '#424259',
    grey4: '#51516c',
    grey5: '#616180',
    grey6: '#727295',
    black: '#ffffff',
  },
  table: {
    background: 'transparent',
    hover: '#4f4259',
    border: 'transparent',
  },
  brand,
  fill: {
    primary: '#171720',
    secondary: '#262635',
    tertiary: '#2f2f42',
    inverted: '#ffffff',
  },
  typography: {
    default: '#efefef',
    inverted: '#1c1c2b',
    subtle: '#848487',
    emphasis: brand.primary,
    placeholder: '#eaeff5',
    disabled: '#eaeff5',
    link: '#ff494a',
    linkVisited: brand.senary,
  },
  validation: {
    error: {
      fill: '#ff7591',
      fillLight: 'transparent',
      text: '#1c1c2b',
      border: '#c34b63',
    },
    info: {
      fill: '#4fc3f7',
      fillLight: 'transparent',
      text: '#1c1c2b',
      border: '#3789ad',
    },
    success: {
      fill: '#2ee68c',
      fillLight: 'transparent',
      text: '#1c1c2b',
      border: '#24a365',
    },
    warning: {
      fill: '#ffee5c',
      fillLight: 'transparent',
      text: '#1c1c2b',
      border: '#c4b63f',
    },
  },
  accessibility: { blue: '#304ffe' },
};

export { LightMode, DarkMode };
